import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelHistoricalImport,
  applyHistoricalImport,
  completeHistoricalImportUpload,
  getHistoricalImportBatch,
  getHistoricalImportBatches,
  getHistoricalImportMemberCandidates,
  mapHistoricalImportAlias,
  reserveHistoricalImportUpload,
  unmapHistoricalImportAlias,
  uploadHistoricalImportCsv,
  validateHistoricalImport,
} from '../lib/historicalImports';
import type {
  HistoricalImportBatchDetail,
  HistoricalImportReserveUploadPayload,
} from '../types';

export const historicalImportKeys = {
  all: ['historical-imports'] as const,
  batches: (squadId: number | string) => [...historicalImportKeys.all, squadId, 'batches'] as const,
  detail: (squadId: number | string, batchId: string) =>
    [...historicalImportKeys.all, squadId, 'detail', batchId] as const,
  members: (squadId: number | string) => [...historicalImportKeys.all, squadId, 'members'] as const,
};

export function useHistoricalImportBatches(squadId: number | string | undefined) {
  return useQuery({
    queryKey: squadId == null ? [...historicalImportKeys.all, 'missing'] : historicalImportKeys.batches(squadId),
    queryFn: () => getHistoricalImportBatches(squadId as number | string),
    enabled: squadId != null,
    retry: false,
  });
}

export function useHistoricalImportBatch(
  squadId: number | string | undefined,
  batchId: string | null
) {
  return useQuery({
    queryKey:
      squadId == null || !batchId
        ? [...historicalImportKeys.all, 'detail', 'missing']
        : historicalImportKeys.detail(squadId, batchId),
    queryFn: () => getHistoricalImportBatch(squadId as number | string, batchId as string),
    enabled: squadId != null && Boolean(batchId),
    retry: false,
  });
}

export function useHistoricalImportMemberCandidates(squadId: number | string | undefined) {
  return useQuery({
    queryKey: squadId == null ? [...historicalImportKeys.all, 'members', 'missing'] : historicalImportKeys.members(squadId),
    queryFn: () => getHistoricalImportMemberCandidates(squadId as number | string),
    enabled: squadId != null,
    retry: false,
  });
}

export function useHistoricalImportActions(squadId: number | string | undefined) {
  const queryClient = useQueryClient();

  const storeDetail = (detail: HistoricalImportBatchDetail) => {
    if (squadId == null) return;
    queryClient.setQueryData(historicalImportKeys.detail(squadId, detail.summary.batchId), detail);
    void queryClient.invalidateQueries({ queryKey: historicalImportKeys.batches(squadId) });
  };

  const upload = useMutation({
    mutationFn: async ({ file, payload }: { file: File; payload: HistoricalImportReserveUploadPayload }) => {
      const reservation = await reserveHistoricalImportUpload(squadId as number | string, payload);
      await uploadHistoricalImportCsv(reservation, file);
      return completeHistoricalImportUpload(squadId as number | string, reservation.batchId);
    },
    onSuccess: storeDetail,
  });

  const mapAlias = useMutation({
    mutationFn: ({ batchId, aliasId, userId }: { batchId: string; aliasId: number; userId: number }) =>
      mapHistoricalImportAlias(squadId as number | string, batchId, aliasId, userId),
    onSuccess: storeDetail,
  });

  const unmapAlias = useMutation({
    mutationFn: ({ batchId, aliasId }: { batchId: string; aliasId: number }) =>
      unmapHistoricalImportAlias(squadId as number | string, batchId, aliasId),
    onSuccess: storeDetail,
  });

  const validate = useMutation({
    mutationFn: (batchId: string) => validateHistoricalImport(squadId as number | string, batchId),
    onSuccess: storeDetail,
  });

  const cancel = useMutation({
    mutationFn: (batchId: string) => cancelHistoricalImport(squadId as number | string, batchId),
    onSuccess: storeDetail,
  });

  const apply = useMutation({
    mutationFn: ({ batchId, idempotencyKey, reason }: { batchId: string; idempotencyKey: string; reason: string }) =>
      applyHistoricalImport(squadId as number | string, batchId, idempotencyKey, reason),
    onSuccess: (result) => {
      if (squadId == null) return;
      void queryClient.invalidateQueries({ queryKey: historicalImportKeys.detail(squadId, result.batchId) });
      void queryClient.invalidateQueries({ queryKey: historicalImportKeys.batches(squadId) });
    },
  });

  return {
    upload: upload.mutateAsync,
    mapAlias: mapAlias.mutateAsync,
    unmapAlias: unmapAlias.mutateAsync,
    validate: validate.mutateAsync,
    cancel: cancel.mutateAsync,
    apply: apply.mutateAsync,
    isUploading: upload.isPending,
    isMapping: mapAlias.isPending || unmapAlias.isPending,
    isValidating: validate.isPending,
    isCancelling: cancel.isPending,
    isApplying: apply.isPending,
  };
}
